import { loadScript } from '#pplx/frontend/common/loadScript.js';
import { ATTACHMENT_EXTENSIONS, AV_EXTENSIONS, SOURCE_EXTENSIONS, } from '#pplx/frontend/components/hooks/useMicrosoftPickerSupport.js';
import { BOX_AVATAR } from '#pplx/frontend/components/utils/consts.js';
import { useCallback, useMemo, useRef } from 'react';
export const BOX_CONTAINER_ID = 'box-file-picker';
const ROOT_FOLDER_ID = '0';
let initPromise = null;
let styleFixesApplied = false;
const loadBoxPickerScript = () => {
    if (initPromise) {
        return initPromise;
    }
    initPromise = new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdn01.boxcdn.net/platform/elements/17.1.0/en-US/picker.css';
        // Order resources are chain-loaded:
        // 1. style sheet
        // 2. picker script
        // 3. style fixes
        link.onload = () => {
            loadScript({
                id: 'box-picker-script',
                src: 'https://cdn01.boxcdn.net/platform/elements/17.1.0/en-US/picker.js',
            }).then(() => {
                if (!styleFixesApplied) {
                    /**
                     * The CSS file loaded from Box contains a couple issues:
                     * 1. The search box within the picker is dark while every other
                     *    component is light.
                     * 2. The icons in the cancel and select buttons are off-center
                     *
                     * I'm fixing this by dynamically injecting styles targeting
                     * the buggy components within the file picker. Although this works,
                     * I feel like this is an extremely hacky solution and am very open
                     * to suggestions.
                     */
                    const boxStyleFixes = document.createElement('style');
                    boxStyleFixes.textContent = `
                  .be .be-header input[type="search"] {
                      background: white;
                  }

                  .btn-content {
                      display: flex;
                      justify-content: center;
                      align-items: center;
                  }
              `;
                    document.head.appendChild(boxStyleFixes);
                    styleFixesApplied = true;
                    resolve();
                }
            }, reject);
        };
        document.head.appendChild(link);
    });
    return initPromise;
};
export const useBoxPicker = ({ mode, isAudioVideoFilesEnabled, onPicked, onCancel, onError, }) => {
    // Ref to store the picker instance
    const pickerRef = useRef(null);
    const openPicker = useCallback(async ({ apiKey, pickerType = 'file' }) => {
        try {
            // This lazy loads the box picker script
            await loadBoxPickerScript();
            const container = document.getElementById(BOX_CONTAINER_ID);
            if (!container) {
                onError?.("Attempted to open picker when container isn't ready");
                return;
            }
            // box has unique extensions for google workspace documents
            let extensions = mode === 'source' ? SOURCE_EXTENSIONS : ATTACHMENT_EXTENSIONS;
            extensions = isAudioVideoFilesEnabled ? extensions.concat(AV_EXTENSIONS) : extensions;
            const box_extensions = [...extensions, '.gdocs', '.gsheets', '.gslides'];
            if (pickerRef.current) {
                pickerRef.current.removeListener('choose', onPicked);
                pickerRef.current.removeListener('cancel', onCancel);
                pickerRef.current.hide();
                pickerRef.current = null;
            }
            let picker;
            if (pickerType === 'folder') {
                picker = new window.Box.FolderPicker();
            }
            else {
                picker = new window.Box.FilePicker();
            }
            pickerRef.current = picker;
            picker.addListener('choose', onPicked);
            picker.addListener('cancel', onCancel);
            picker.show(ROOT_FOLDER_ID, apiKey, {
                container: `#${BOX_CONTAINER_ID}`,
                logoUrl: BOX_AVATAR,
                extensions: box_extensions.join(','),
                canUpload: false,
                canSetShareAccess: false,
                canCreateNewFolder: false,
                chooseButtonLabel: 'Select',
                cancelButtonLabel: 'Cancel',
            });
        }
        catch (error) {
            onError?.(error);
            return;
        }
    }, [onError, mode, onCancel, onPicked, isAudioVideoFilesEnabled]);
    const cleanupPicker = useCallback(() => {
        if (pickerRef.current) {
            pickerRef.current.removeListener('choose', onPicked);
            pickerRef.current.removeListener('cancel', onCancel);
            pickerRef.current.hide();
            pickerRef.current = null;
        }
    }, [onPicked, onCancel]);
    return useMemo(() => ({ openPicker, cleanupPicker }), [openPicker, cleanupPicker]);
};
//# sourceMappingURL=useBoxPicker.js.map